import { Space, Spin } from "antd";
import { AnalyticsChartsGrid } from "./components/AnalyticsChartsGrid";
import { useQueries } from "@tanstack/react-query";
import { fetchAnalyticsData } from "../../../lib/api/resources/analytics";
import { AnalyticsDataType } from "./types/AnalyticsDataType.interface";
import { useSearchParams } from "react-router-dom";
import { AnalyticsDateRangePicker } from "./components/AnalyticsDateRangePicker";
import { useEffect } from "react";
import { format, subDays, isValid, parseISO } from "date-fns";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";

export const AnalyticsOverview = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const from = searchParams.get("from");
  const to = searchParams.get("to");
  const currentUser = useCurrentUser();
  const permissions = currentUser?.permissions || [];

  useEffect(() => {
    if (!from || !to || !isValid(parseISO(from)) || !isValid(parseISO(to))) {
      const today = new Date();
      const last30Days = subDays(today, 30);
      setSearchParams({
        from: format(last30Days, "yyyy-MM-dd"),
        to: format(today, "yyyy-MM-dd"),
      });
    }
  }, [from, to, setSearchParams]);

  const analyticsDataTypePermissions: Record<AnalyticsDataType, string> = {
    ownership: "admin_data_analytics_ownership_read",
    brand: "admin_data_analytics_brand_read",
    "lease-company": "admin_data_analytics_lease_company_read",
    "has-images": "admin_data_analytics_has_images_read",
    "has-repair-appointment":
      "admin_data_analytics_has_repair_appointment_read",
    "cause-of-damage": "admin_data_analytics_cause_of_damage_read",
    "service-partner": "admin_data_analytics_service_partner_read",
    "service-partner-chain": "admin_data_analytics_service_partner_chain_read",
    "service-partner-preferred":
      "admin_data_analytics_service_partner_preferred_read",
    "dossier-type": "admin_data_analytics_dossier_type_read",
    source: "admin_data_analytics_source_read",
    "dealership-location": "admin_data_analytics_dealership_location_read",
    "total-dossiers": "admin_data_analytics_total_dossiers_read",
  };

  const allowedAnalyticsDataTypes = (
    Object.keys(analyticsDataTypePermissions) as AnalyticsDataType[]
  ).filter((type) => permissions.includes(analyticsDataTypePermissions[type]));

  const analyticsQueries = useQueries({
    queries: allowedAnalyticsDataTypes.map((type) => ({
      queryKey: ["analytics", type, from, to],
      queryFn: () => fetchAnalyticsData({ analyticsDataType: type, from, to }),
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });

  if (analyticsQueries.isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (analyticsQueries.isError) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <p>Er is iets mis gegaan met het laden van de management informatie.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space size="middle" wrap>
          <AnalyticsDateRangePicker />
        </Space>
      </div>

      <AnalyticsChartsGrid data={analyticsQueries?.data} />
    </div>
  );
};
